import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "./SEO";
import Navbar from "./layout/Navbar";
import Footer from "./layout/Footer";
import FloatingActions from "./layout/FloatingActions";
import useFavicon from "../hooks/useFavicon";
import LazyImage from "./ui/LazyImage";
import LazyVideo from "./ui/LazyVideo";

// Bespoke premium rotating words animation for the Testimonials Hero Heading
function RotatingHeading() {
  const words = ["Success", "Capability", "Achievement", "Alumni Growth"];
  const [wordIdx, setWordIdx] = useState(0);
  const [subText, setSubText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;
    const currentWord = words[wordIdx];
    const typingSpeed = isDeleting ? 40 : 85;

    if (isDeleting) {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length - 1));
      }, typingSpeed);
    } else {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length + 1));
      }, typingSpeed);
    }

    if (!isDeleting && subText === currentWord) {
      timer = setTimeout(() => setIsDeleting(true), 2200);
    } else if (isDeleting && subText === "") {
      setIsDeleting(false);
      setWordIdx((prev) => (prev + 1) % words.length);
    }

    return () => clearTimeout(timer);
  }, [subText, isDeleting, wordIdx]);

  return (
    <span className="inline-block relative py-1 bg-gradient-to-r from-[#C6A25E] to-amber-300 bg-clip-text text-transparent font-extrabold tracking-tight min-w-[120px] min-[360px]:min-w-[180px] sm:min-w-[240px] md:min-w-[320px] lg:min-w-[360px]">
      {subText}
      <span className="border-r-[3.5px] border-[#C6A25E] ml-1.5 animate-pulse" style={{ verticalAlign: 'middle' }}></span>
    </span>
  );
}

export default function Testimonials() {
  useFavicon('/logo-main.png');
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [subFilter, setSubFilter] = useState("all-junior");
  const [subSubFilter, setSubSubFilter] = useState("all");
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);

  // Reset pagination when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, subFilter, subSubFilter]);

  // Counter animation utility for stats
  const [counters, setCounters] = useState({ students: 0, outcomes: 0, experience: 0 });

  useEffect(() => {
    let start = 0;
    const duration = 1200;
    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      setCounters({
        students: Math.floor(progress * 500),
        outcomes: Math.floor(progress * 95),
        experience: Math.floor(progress * 7)
      });

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, []);

  const reviewsList = [
    {
      name: "Mohd Uzair",
      role: "EAPCET MPC Student",
      category: "junior",
      tag: "Cervello Junior Academy (2025-2026)",
      avatar: "/avatar-normal-boy.webp",
      rating: 5,
      text: "The conceptual clarity I got here for mathematics and physics made a difference. The focus on solving previous years' questions helped me secure a 26,000 rank in EAPCET!",
      outcomeLabel: "EAPCET Stream & Rank",
      beforeAfter: "MPC Stream – EAPCET 2026: 26,000 Rank",
      subFilters: ["eapcet"]
    },
    {
      name: "Simran Fatima",
      role: "EAPCET BiPC Student (EAPCET Batch)",
      category: "junior",
      tag: "Cervello Junior Academy (2025-2026)",
      avatar: "/avatar-hijab-girl.webp",
      rating: 5,
      text: "Being part of the EAPCET batch was amazing. The focused study planner and interactive biology sessions helped me secure a 29,197 rank in EAPCET!",
      outcomeLabel: "EAPCET Stream & Rank",
      beforeAfter: "BiPC Stream – EAPCET 2026: 29,197 Rank",
      subFilters: ["eapcet"]
    },
    {
      name: "Ayesha",
      role: "BiPC 1st Year Student",
      category: "junior",
      tag: "Cervello Junior Academy (2025-2026)",
      avatar: "/avatar-hijab-girl.webp",
      rating: 5,
      text: "The conceptual approach to Zoology and Botany is what helped me score a massive 95% in my 1st Year BiPC boards! Best intermediate coaching ever.",
      outcomeLabel: "Board Exam Score",
      beforeAfter: "BiPC Stream – 95% Board Score (2025-2026)",
      subFilters: ["intermediate", "inter-bipc", "first-year"]
    },
    {
      name: "Usama",
      role: "MPC 1st Year Student",
      category: "junior",
      tag: "Cervello Junior Academy (2025-2026)",
      avatar: "/avatar-normal-boy.webp",
      rating: 5,
      text: "The conceptual clarity in Mathematics and Physics made learning so interesting. Getting 80% in my MPC 1st Year boards is a great start, all thanks to Cervello Junior Academy!",
      outcomeLabel: "Board Exam Score",
      beforeAfter: "MPC Stream – 80% Board Score (2025-2026)",
      subFilters: ["intermediate", "inter-mpc", "first-year"]
    },
    {
      name: "Ayesha Simra Aleem",
      role: "BiPC 1st Year Student",
      category: "junior",
      tag: "Cervello Junior Academy (2025-2026)",
      avatar: "/avatar-hijab-girl.webp",
      rating: 5,
      text: "Excellent faculty and continuous evaluation. Scoring 88% in my BiPC 1st Year boards has given me so much confidence for EAPCET preparation.",
      outcomeLabel: "Board Exam Score",
      beforeAfter: "BiPC Stream – 88% Board Score (2025-2026)",
      subFilters: ["intermediate", "inter-bipc", "first-year"]
    },
    {
      name: "Samreen",
      role: "MPC 2nd Year Student (Free Board Support)",
      category: "junior",
      tag: "Cervello Junior Academy (2025-2026)",
      avatar: "/avatar-hijab-girl.webp",
      rating: 5,
      text: "I was part of the free board support program, and it helped me prepare thoroughly for the intermediate exams. Scoring 70% in my MPC 2nd Year boards was a huge relief and achievement.",
      outcomeLabel: "Board Exam Score",
      beforeAfter: "MPC Stream – 70% Board Score (2025-2026)",
      subFilters: ["intermediate", "inter-mpc", "second-year"]
    },
    {
      name: "Mohd Imran",
      role: "MPC 2nd Year Student (Boards & EAPCET)",
      category: "junior",
      tag: "Cervello Junior Academy (2025-2026)",
      avatar: "/avatar-normal-boy.webp",
      rating: 5,
      text: "Being in both the board preparation and EAPCET batch was highly beneficial. The parallel test series helped me score 70% in my 2nd-year boards and get a 60,000 EAPCET rank!",
      outcomeLabel: "Boards & EAPCET Rank",
      beforeAfter: "70% Boards (25-26) | EAPCET 2026: 60K",
      subFilters: ["intermediate", "eapcet", "inter-mpc", "second-year"]
    },
    {
      name: "Jasmine Sultana",
      role: "EAPCET BiPC Student",
      category: "junior",
      tag: "Cervello Junior Academy (2025-2026)",
      avatar: "/avatar-hijab-girl.webp",
      rating: 5,
      text: "The practice mocks and regular feedback sessions gave me the confidence I needed. Securing a 54,276 rank in EAPCET is a huge milestone for me, thanks to Junior Academy!",
      outcomeLabel: "EAPCET Stream & Rank",
      beforeAfter: "BiPC Stream – EAPCET 2026: 54,276 Rank",
      subFilters: ["eapcet"]
    },
    {
      name: "Syed Bilal",
      role: "Class 10th Student",
      category: "junior",
      tag: "Cervello Junior Academy (2025-2026)",
      avatar: "/avatar-normal-boy.webp",
      rating: 5,
      text: "The conceptual understanding of Mathematics and Physics was key to my success. Scoring a 9.8 GPA in my SSC Board exams was a proud moment for me and my family, thanks to Cervello Junior Academy!",
      outcomeLabel: "SSC Board GPA",
      beforeAfter: "Class 10th – 9.8 GPA Board Score (2025-2026)",
      subFilters: ["class10"]
    },
    {
      name: "Fatima Begum",
      role: "Class 10th Student",
      category: "junior",
      tag: "Cervello Junior Academy (2025-2026)",
      avatar: "/avatar-hijab-girl.webp",
      rating: 5,
      text: "Cervello Junior Academy helped me clear all my science doubts. I scored a 9.5 GPA in the 10th class boards. Highly recommend the foundation batch!",
      outcomeLabel: "SSC Board GPA",
      beforeAfter: "Class 10th – 9.5 GPA Board Score (2025-2026)",
      subFilters: ["class10"]
    }
  ];

  const filteredReviews = reviewsList.filter(item => {
    if (filter === "all") return true;
    if (filter === "junior") {
      if (subFilter === "all-junior") return item.category === "junior";
      if ((subFilter === "inter-mpc" || subFilter === "inter-bipc") && subSubFilter !== "all") {
        return item.category === "junior" &&
          item.subFilters &&
          item.subFilters.includes(subFilter) &&
          item.subFilters.includes(subSubFilter);
      }
      return item.category === "junior" && item.subFilters && item.subFilters.includes(subFilter);
    }
    return item.category === filter;
  });

  const ITEMS_PER_PAGE = 6;
  const totalItems = filteredReviews.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedReviews = filteredReviews.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <React.Fragment>
      <SEO
        title="Student Success Stories & Testimonials | Cervello Eduverse"
        description="Read real student reviews, parent feedback, and startup founder testimonials about their learning journeys and career outcomes at Cervello Eduverse."
        keywords="student reviews, Cervello reviews, success stories, parent feedback, intermediate coaching reviews, coding bootcamps reviews Hyderabad, customer feedback, client reviews"
        url="https://cervelloeduverse.com/testimonials"
        canonical="https://cervelloeduverse.com/testimonials"
      />

      <div className="font-inter bg-nm-light text-[#1E293B] min-h-screen relative">
        <Navbar />

        {/* HERO SECTION */}
        <section className="relative overflow-hidden pt-20 pb-20 bg-nm-dark text-[#0B1C2D] border-b border-slate-200/20">
          {/* Responsive Brand Video Background */}
          <LazyVideo
            src={isMobile ? "/videos/testimonals-mobile.mp4" : "/videos/testimonals-desktop.mp4"}
            poster="/logo-main.webp"
            className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none select-none opacity-[0.25]"
            placeholderBg="bg-transparent"
          />

          <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000002_1px,transparent_1px),linear-gradient(to_bottom,#00000003_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none z-10" />
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#C6A25E]/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 pointer-events-none z-10" />

          <div className="relative z-20 max-w-7xl mx-auto px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-dark border-transparent text-[#0B1C2D] text-xs font-semibold mb-6">
              <span className="w-2 h-2 rounded-full bg-[#C6A25E] animate-pulse" />
              <span>Real Stories, Real Capability</span>
            </div>
            <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl font-bold mb-6 tracking-tight text-[#0B1C2D]">
              <span className="block">Voices of</span>
              <span className="block mt-2"><RotatingHeading /></span>
            </h1>
            <p className="text-slate-700 text-base sm:text-lg max-w-2xl mx-auto font-light mb-12">
              Discover how school students, engineering trainees, and startup founders achieve their goals through our concept-first mentorship model.
            </p>

            {/* Live Counters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto mt-10">
              <div className="glass-card-dark border-transparent rounded-2xl p-6">
                <div className="text-3xl sm:text-4xl font-extrabold text-[#C6A25E]">{counters.students}+</div>
                <div className="text-xs sm:text-sm text-slate-600 mt-1 uppercase font-semibold tracking-wider">Students Trained</div>
              </div>
              <div className="glass-card-dark border-transparent rounded-2xl p-6">
                <div className="text-3xl sm:text-4xl font-extrabold text-[#C6A25E]">{counters.outcomes}%</div>
                <div className="text-xs sm:text-sm text-slate-600 mt-1 uppercase font-semibold tracking-wider">Goal Outcomes Met</div>
              </div>
              <div className="glass-card-dark border-transparent rounded-2xl p-6">
                <div className="text-3xl sm:text-4xl font-extrabold text-[#C6A25E]">{counters.experience}+ Years</div>
                <div className="text-xs sm:text-sm text-slate-600 mt-1 uppercase font-semibold tracking-wider">Founder's Experience</div>
              </div>
            </div>
          </div>
        </section>

        {/* REVIEWS GRID & FILTER */}
        <section className="py-20 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            {/* Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {[
                { id: "all", label: "All Achievements" },
                { id: "junior", label: "Junior Academy" },
                { id: "elevare", label: "Elevare Academy" },
                { id: "business", label: "Business Lab" }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setFilter(tab.id);
                    setSubFilter("all-junior");
                  }}
                  className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer ${filter === tab.id
                    ? "nm-flat-light text-[#C6A25E] font-bold border-transparent"
                    : "nm-button-light text-slate-600 border-transparent"
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Junior Academy Sub-Filters */}
            {filter === "junior" && (
              <div className="flex flex-wrap justify-center gap-2 mb-8 max-w-4xl mx-auto py-3.5 px-4 nm-inset-light rounded-2xl border-transparent animate-fade-in">
                <span className="w-full text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5 block">
                  Filter by Program / Class
                </span>
                {[
                  { id: "all-junior", label: "All Junior" },
                  { id: "class10", label: "Class 10th" },
                  { id: "intermediate", label: "Intermediate" },
                  { id: "eapcet", label: "EAPCET" },
                  { id: "inter-mpc", label: "Inter MPC" },
                  { id: "inter-bipc", label: "Inter BiPC" }
                ].map(subTab => (
                  <button
                    key={subTab.id}
                    onClick={() => {
                      setSubFilter(subTab.id);
                      setSubSubFilter("all");
                    }}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer ${subFilter === subTab.id
                      ? "nm-flat-light text-[#C6A25E] font-bold border-transparent"
                      : "nm-button-light text-slate-500 border-transparent"
                      }`}
                  >
                    {subTab.label}
                  </button>
                ))}
              </div>
            )}

            {/* MPC / BiPC Conditional Sub-Sub-Filters */}
            {filter === "junior" && (subFilter === "inter-mpc" || subFilter === "inter-bipc") && (
              <div className="flex flex-wrap justify-center gap-2 mb-12 max-w-2xl mx-auto py-3 px-4 nm-inset-light rounded-2xl border-transparent animate-fade-in">
                <span className="w-full text-center text-[9px] font-bold text-[#C6A25E] uppercase tracking-widest mb-2 block">
                  Select Year for {subFilter === "inter-mpc" ? "MPC" : "BiPC"}
                </span>
                {[
                  { id: "all", label: subFilter === "inter-mpc" ? "All MPC" : "All BiPC" },
                  { id: "first-year", label: "1st Year" },
                  { id: "second-year", label: "2nd Year" }
                ].map(subSubTab => (
                  <button
                    key={subSubTab.id}
                    onClick={() => setSubSubFilter(subSubTab.id)}
                    className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all duration-200 cursor-pointer ${subSubFilter === subSubTab.id
                      ? "nm-flat-light text-[#C6A25E] font-bold border-transparent"
                      : "nm-button-light text-slate-400 border-transparent"
                      }`}
                  >
                    {subSubTab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Testimonials Grid */}
            {paginatedReviews.length > 0 ? (
              <div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in">
                  {paginatedReviews.map((review, index) => (
                    <div
                      key={index}
                      className="nm-flat-light rounded-3xl p-8 border-transparent hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between"
                    >
                      <div>
                        {/* Header */}
                        <div className="flex justify-between items-start mb-6">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl overflow-hidden nm-inset-light flex items-center justify-center text-2xl flex-shrink-0">
                              {review.avatar.startsWith('/') ? (
                                <LazyImage src={review.avatar} alt={review.name} className="w-full h-full object-cover" />
                              ) : (
                                review.avatar
                              )}
                            </div>
                            <div>
                              <h4 className="font-bold text-[#0B1C2D]">{review.name}</h4>
                              <p className="text-xs text-slate-400 font-medium">{review.role}</p>
                            </div>
                          </div>
                          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${review.category === 'junior' ? 'nm-inset-light text-sky-600' :
                            review.category === 'elevare' ? 'nm-inset-light text-blue-600' : 'nm-inset-light text-amber-600'
                            }`}>
                            {review.tag}
                          </span>
                        </div>

                        {/* Ratings */}
                        <div className="flex gap-1 mb-4">
                          {[...Array(review.rating)].map((_, i) => (
                            <span key={i} className="text-[#C6A25E] text-base">★</span>
                          ))}
                        </div>

                        {/* Quote */}
                        <p className="text-slate-600 text-sm leading-relaxed font-light mb-6 italic">
                          "{review.text}"
                        </p>
                      </div>

                      {/* Outcome display */}
                      <div className="border-t border-slate-200/30 pt-5 mt-4">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">{review.outcomeLabel}</span>
                        <div className="font-semibold text-sm text-[#0B1C2D] mt-1.5 flex items-center gap-1.5">
                          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                          {review.beforeAfter}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-16 border-t border-slate-200/30 pt-8 animate-fade-in">
                    <span className="text-xs sm:text-sm text-slate-400 font-semibold">
                      Showing <span className="text-[#0B1C2D]">{startIndex + 1}</span> to{" "}
                      <span className="text-[#0B1C2D]">
                        {Math.min(startIndex + ITEMS_PER_PAGE, totalItems)}
                      </span>{" "}
                      of <span className="text-[#0B1C2D]">{totalItems}</span> achievements
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 rounded-xl text-xs font-bold nm-button-light text-slate-600 border-transparent disabled:opacity-40 active:scale-95 transition-all cursor-pointer"
                      >
                        ← Previous
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-9 h-9 rounded-xl text-xs font-bold transition-all active:scale-95 cursor-pointer ${currentPage === pageNum
                            ? "nm-flat-light text-[#C6A25E] font-bold border-transparent"
                            : "nm-button-light text-slate-500 border-transparent"
                            }`}
                        >
                          {pageNum}
                        </button>
                      ))}

                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 rounded-xl text-xs font-bold nm-button-light text-slate-600 border-transparent disabled:opacity-40 active:scale-95 transition-all cursor-pointer"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16 nm-flat-light rounded-3xl border-transparent max-w-xl mx-auto animate-fade-in">
                <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto text-2xl mb-4 border border-amber-100">
                  ✨
                </div>
                <h3 className="text-lg font-bold text-[#0B1C2D] mb-2">Success Stories Coming Soon</h3>
                <p className="text-slate-500 text-sm font-light px-6">
                  We are currently launching our initial batches. Authentic reviews and parent/student success stories will be published here as our learners graduate and achieve their goals.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* CALL TO ACTION */}
        <section className="py-20 bg-nm-light border-t border-slate-200/40 text-center">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#0B1C2D]">
              Start Your Own Success Story Today
            </h2>
            <p className="text-slate-500 text-sm max-w-lg mx-auto mb-8 font-light leading-relaxed">
              Book the trial option that fits your needs: Free 1-Day Demo for school & intermediate prep, 7-Day Demo (₹99) for career tracks, or a free strategy consultation.
            </p>
            <a
              href="/book-demo"
              className="inline-block nm-button-light text-[#0B1C2D] px-8 py-4 rounded-xl font-bold border-transparent active:scale-98 transition-all duration-300 cursor-pointer"
            >
              👉 Register Trial / Consultation Session
            </a>
          </div>
        </section>

        <Footer />
        <FloatingActions />
      </div>
    </React.Fragment>
  );
}
